import React, { useEffect, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, doc, getDoc, getDocs, updateDoc, arrayUnion, deleteDoc } from "firebase/firestore";
import { db, auth, storage } from "./firebase";
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useTranslation } from 'react-i18next';
import PremiumReportRenderer from './components/PremiumReportRenderer';
import EditableSectionRenderer from './components/EditableSectionRenderer';
import SignatureModal from './components/SignatureModal';
import RemediationNoteModal from './components/RemediationNoteModal';
import RemediationSignoffModal from './components/RemediationSignoffModal';
import webLogo from "./assets/image/logo_web.png";
import Banner from './components/Banner';
import { getCurrentUserCompanyId, getCurrentUserProfile } from "./utils/userUtils";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import html2pdf from "html2pdf.js";
import { buildSubmissionChangeLog, buildSubmissionEditMetadata } from "./utils/submissionAudit";
import { filterVisibleWorkflowFields } from "./utils/workflowUtils";

// Helper function to resolve asset name from multiple possible locations
const resolveAssetName = (asset) => {
  if (!asset) return "Unnamed Asset";

  // Preferred: title field
  if (asset.title) return asset.title;

  // Fallback: explicit name property
  if (asset.name) return asset.name;

  // Fallback: check dynamic fields array for "name" field
  if (Array.isArray(asset.fields)) {
    const nameField = asset.fields.find(
      f => f.label?.toLowerCase() === "name" || f.label?.toLowerCase() === "title"
    );
    if (nameField?.value) return nameField.value;
  }

  return "Unnamed Asset";
};

export default function ReportsNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [completedChecks, setCompletedChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [quickPreview, setQuickPreview] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterFormId, setFilterFormId] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [forms, setForms] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedResponses, setEditedResponses] = useState({});
  const [showChangeLog, setShowChangeLog] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [currentSignatureField, setCurrentSignatureField] = useState(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [currentNoteField, setCurrentNoteField] = useState(null);
  const [signoffModalOpen, setSignoffModalOpen] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const visibleReportFields = useMemo(
    () => filterVisibleWorkflowFields(selectedReport?.formDef || [], userProfile),
    [selectedReport?.formDef, userProfile]
  );
  const hiddenWorkflowCount = useMemo(() => {
    const totalSections = (selectedReport?.formDef || []).filter(
      (field) => field?.type === "section" || field?.type === "split_workflow"
    ).length;
    const visibleSections = visibleReportFields.filter(
      (field) => field?.type === "section" || field?.type === "split_workflow"
    ).length;
    return Math.max(0, totalSections - visibleSections);
  }, [selectedReport?.formDef, visibleReportFields]);

  // Check if report is locked (approved reports cannot be edited)
  const isReportLocked = (report) => {
    return report?.approvalStatus === 'approved' || report?.status === 'approved';
  };

  // Generate AI summary of changes
  const generateChangeSummary = (changeLog) => {
    if (!changeLog || changeLog.length === 0) return null;
    
    const uniqueFields = new Set(changeLog.map(c => c.field));
    const uniqueEditors = new Set(changeLog.map(c => c.changedBy));
    const fieldCounts = {};
    
    changeLog.forEach(c => {
      fieldCounts[c.field] = (fieldCounts[c.field] || 0) + 1;
    });
    
    const mostEditedField = Object.entries(fieldCounts)
      .sort((a, b) => b[1] - a[1])[0];
    
    return {
      totalChanges: changeLog.length,
      uniqueFields: uniqueFields.size,
      uniqueEditors: uniqueEditors.size,
      mostEdited: mostEditedField ? `${mostEditedField[0]} (${mostEditedField[1]}x)` : 'N/A',
      summary: `${changeLog.length} field${changeLog.length > 1 ? 's' : ''} modified post-submission by ${uniqueEditors.size} user${uniqueEditors.size > 1 ? 's' : ''}`
    };
  };

  // Add remediation note
  const handleAddNote = async (fieldId, noteText) => {
    if (!selectedReport?.id) return;
    
    const companyId = await getCurrentUserCompanyId();
    const submissionRef = doc(db, `companies/${companyId}/submissions/${selectedReport.id}`);
    
    try {
      await updateDoc(submissionRef, {
        [`remediation.${fieldId}.notes`]: arrayUnion({
          id: `note_${Date.now()}`,
          text: noteText,
          createdBy: auth.currentUser.email,
          createdAt: new Date()
        }),
        ...buildSubmissionEditMetadata(selectedReport, "web"),
      });
      
      // Reload report
      const updatedDoc = await getDoc(submissionRef);
      if (updatedDoc.exists()) {
        await loadReportWithForm({ id: selectedReport.id, ...updatedDoc.data() });
      }
    } catch (err) {
      console.error('Failed to add note:', err);
      alert('Failed to add note: ' + err.message);
    }
  };

  // Add remediation attachment
  const handleAddAttachment = async (fieldId) => {
    if (!selectedReport?.id) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setUploadingAttachment(true);
      
      try {
        const companyId = await getCurrentUserCompanyId();
        
        // Upload file to storage
        const storageRef = ref(
          storage,
          `companies/${companyId}/submissions/${selectedReport.id}/remediation/${fieldId}/${Date.now()}_${file.name}`
        );
        
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        
        // Add to Firestore
        const submissionRef = doc(db, `companies/${companyId}/submissions/${selectedReport.id}`);
        
        await updateDoc(submissionRef, {
          [`remediation.${fieldId}.attachments`]: arrayUnion({
            url,
            name: file.name,
            uploadedBy: auth.currentUser.email,
            uploadedAt: new Date()
          }),
          ...buildSubmissionEditMetadata(selectedReport, "web"),
        });
        
        // Reload report
        const updatedDoc = await getDoc(submissionRef);
        if (updatedDoc.exists()) {
          await loadReportWithForm({ id: selectedReport.id, ...updatedDoc.data() });
        }
      } catch (err) {
        console.error('Failed to upload attachment:', err);
        alert('Failed to upload file: ' + err.message);
      } finally {
        setUploadingAttachment(false);
      }
    };
    
    input.click();
  };

  // Final sign-off
  const handleSignoff = async (signatureData) => {
    if (!selectedReport?.id) return;
    
    const companyId = await getCurrentUserCompanyId();
    const submissionRef = doc(db, `companies/${companyId}/submissions/${selectedReport.id}`);
    
    try {
      await updateDoc(submissionRef, {
        remediationSignoff: {
          signed: true,
          signedBy: auth.currentUser.email,
          signedAt: new Date(),
          signatureUrl: signatureData.signatureUrl,
          comment: signatureData.comment
        },
        status: 'remediated',
        ...buildSubmissionEditMetadata(selectedReport, "web"),
      });
      
      // Reload report
      const updatedDoc = await getDoc(submissionRef);
      if (updatedDoc.exists()) {
        await loadReportWithForm({ id: selectedReport.id, ...updatedDoc.data() });
      }
      alert('✔ Report signed off and locked');
    } catch (err) {
      console.error('Failed to sign off:', err);
      alert('Failed to sign off: ' + err.message);
    }
  };

  // Check if report can be remediated
  const canRemediateReport = () => {
    if (!userProfile || !selectedReport) return false;
    if (selectedReport.remediationSignoff?.signed) return false; // Already signed off
    return userProfile.role === 'admin' || userProfile.role === 'owner';
  };

  // Delete submission (admin only)
  const handleDeleteSubmission = async (submission) => {
    if (!canEditReports()) {
      alert('Only admins can delete submissions');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete this submission?\n\n"${getTitle(submission)}"\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const companyId = await getCurrentUserCompanyId();
      const submissionRef = doc(db, `companies/${companyId}/submissions/${submission.id}`);
      await deleteDoc(submissionRef);
      
      // If currently viewing this report, clear selection
      if (selectedReport?.id === submission.id) {
        setSelectedReport(null);
        setEditMode(false);
      }
      
      alert('✅ Submission deleted successfully');
    } catch (err) {
      console.error('Failed to delete submission:', err);
      alert('❌ Failed to delete submission: ' + err.message);
    }
  };

  // Delete all selected submissions (admin only)
  const handleDeleteSelected = async () => {
    if (!canEditReports()) {
      alert('Only admins can delete submissions');
      return;
    }

    if (selectedIds.length === 0) {
      alert('Please select reports to delete');
      return;
    }

    const count = selectedIds.length;
    if (!window.confirm(`⚠️ WARNING: You are about to delete ${count} submission${count > 1 ? 's' : ''}.\n\nThis action CANNOT be undone.\n\nAre you absolutely sure?`)) {
      return;
    }

    try {
      const companyId = await getCurrentUserCompanyId();
      let deletedCount = 0;
      let failedCount = 0;

      for (const id of selectedIds) {
        try {
          const submissionRef = doc(db, `companies/${companyId}/submissions/${id}`);
          await deleteDoc(submissionRef);
          deletedCount++;
          
          // If currently viewing one of these reports, clear selection
          if (selectedReport?.id === id) {
            setSelectedReport(null);
            setEditMode(false);
          }
        } catch (err) {
          console.error(`Failed to delete ${id}:`, err);
          failedCount++;
        }
      }

      setSelectedIds([]);
      
      if (failedCount === 0) {
        alert(`✅ Successfully deleted ${deletedCount} submission${deletedCount > 1 ? 's' : ''}`);
      } else {
        alert(`⚠️ Deleted ${deletedCount} submission${deletedCount > 1 ? 's' : ''}.\nFailed to delete ${failedCount}.`);
      }
    } catch (err) {
      console.error('Failed to delete submissions:', err);
      alert('❌ Failed to delete submissions: ' + err.message);
    }
  };

  useEffect(() => {
    const loadCompanyAndSubmissions = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load user profile to check role
        const profile = await getCurrentUserProfile();
        setUserProfile(profile);

        const userCompanyId = await getCurrentUserCompanyId();
        if (!userCompanyId) {
          setError("No company assigned to your account");
          setLoading(false);
          return;
        }

        // Load forms for quick preview
        const formsRef = collection(db, `companies/${userCompanyId}/forms`);
        const formsSnap = await getDocs(formsRef);
        const formsList = formsSnap.docs.map(d => ({
          id: d.id,
          ...d.data(),
          fields: normalizeFields(d.data().fields)
        }));
        setForms(formsList);

        // Load company logo if set
        try {
          const companyDoc = await getDoc(doc(db, 'companies', userCompanyId));
          if (companyDoc.exists() && companyDoc.data().logoURL) {
            setCompanyLogo(companyDoc.data().logoURL);
          }
        } catch (err) {
          console.warn('Could not load company logo:', err);
        }

        const collRef = collection(db, "companies", userCompanyId, "submissions");
        let q;
        try {
          q = query(collRef, orderBy("createdAt", "desc"));
        } catch (e) {
          q = collRef;
        }

        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            const items = snapshot.docs
              .map((doc) => ({ id: doc.id, ...doc.data() }))
              .filter((item) => item.status !== "draft"); // Filter out drafts
            setCompletedChecks(items);
            setLoading(false);
          },
          (err) => {
            console.error("Failed to load submissions:", err);
            setError("Failed to load submissions.");
            setLoading(false);
          }
        );

        return () => unsubscribe();
      } catch (err) {
        console.error(err);
        setError("Failed to initialize submissions listener.");
        setLoading(false);
      }
    };
    loadCompanyAndSubmissions();
  }, []);

  const normalizeFields = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
      // Recursively normalize, preserving section children
      return raw.map(field => {
        if (field.type === 'section' || field.type === 'split_workflow') {
          // Normalize children/fields using the non-empty child list.
          const childFields = Array.isArray(field.children) && field.children.length > 0
            ? field.children
            : Array.isArray(field.fields)
              ? field.fields
              : [];
          return {
            ...field,
            fields: normalizeFields(childFields) // Preserve child fields
          };
        }
        return field;
      });
    }
    if (typeof raw === 'string') {
      try { return normalizeFields(JSON.parse(raw)); } catch { return []; }
    }
    if (typeof raw === 'object') {
      return Object.entries(raw).map(([k, v]) => (v && typeof v === 'object' ? { id: v.id || k, ...v } : { id: k, question: String(v) }));
    }
    return [];
  };

  const loadReportWithForm = async (submission) => {
    if (!submission?.companyId || !submission?.formId) {
      setSelectedReport(submission);
      setEditedResponses(submission.responses || submission.answers || {});
      return;
    }
    setLoadingReport(true);
    try {
      // ✅ PRIORITY 1: Use the form snapshot stored in the submission (preserves answers even if form is edited)
      if (submission.formDef && Array.isArray(submission.formDef) && submission.formDef.length > 0) {
        console.log('✅ Using stored formDef from submission (preserves original field structure)');
        const formDef = normalizeFields(submission.formDef);
        setSelectedReport({ ...submission, formDef });
        setEditedResponses(submission.responses || submission.answers || {});
        setEditMode(false);
        setLoadingReport(false);
        return;
      }

      // ✅ FALLBACK: If no formDef in submission, fetch current form (for old submissions)
      console.log('⚠️ No formDef in submission, fetching current form (may lose answers if form was edited)');
      const formRef = doc(db, `companies/${submission.companyId}/forms/${submission.formId}`);
      const formSnap = await getDoc(formRef);
      let formDef = null;
      if (formSnap.exists()) {
        const rawFields = formSnap.data().fields;
        console.log('🔍 RAW FORM DATA from Firestore:', {
          formId: submission.formId,
          rawFields,
          isArray: Array.isArray(rawFields),
          fieldCount: Array.isArray(rawFields) ? rawFields.length : 'N/A',
          firstField: Array.isArray(rawFields) ? rawFields[0] : null,
          sectionsFound: Array.isArray(rawFields) ? rawFields.filter(f => f.type === 'section') : []
        });
        formDef = normalizeFields(rawFields);
        console.log('🔍 NORMALIZED FORM:', {
          normalizedCount: formDef.length,
          normalizedFields: formDef,
          sectionsAfterNormalize: formDef.filter(f => f.type === 'section')
        });
      }
      setSelectedReport({ ...submission, formDef });
      setEditedResponses(submission.responses || submission.answers || {});
      setEditMode(false);
    } catch (e) {
      console.error('Failed to load form definition for report', e);
      setSelectedReport(submission);
      setEditedResponses(submission.responses || submission.answers || {});
    } finally {
      setLoadingReport(false);
    }
  };

  // Check if user can edit (admin or super user)
  const canEditReports = () => {
    if (!userProfile) return false;
    const role = userProfile.role?.toLowerCase() || '';
    return role === 'admin' || role === 'super' || role === 'superuser' || role === 'super user';
  };

  // Handle adding new signature (append-only)
  const handleAddSignature = (dataUrl, reason) => {
    if (!currentSignatureField || !auth.currentUser) return;

    const fieldId = currentSignatureField.id;
    const currentValue = editedResponses[fieldId]?.value || {};
    
    // Handle both instances array and legacy signatureUrl format
    let existingInstances = currentValue.instances || [];
    if (currentValue.signatureUrl && existingInstances.length === 0) {
      // Convert legacy mobile signature to instance format
      existingInstances = [{
        url: currentValue.signatureUrl,
        createdAt: currentValue.createdAt || new Date().toISOString(),
        createdBy: currentValue.createdBy || 'mobile-user'
      }];
    }

    const newInstance = {
      url: dataUrl,
      createdAt: new Date().toISOString(),
      createdBy: auth.currentUser.email,
      afterSubmission: true,
      reason: reason || undefined
    };

    handleFieldEdit(fieldId, {
      instances: [...existingInstances, newInstance]
    });

    setCurrentSignatureField(null);
  };

  // Handle field value change in edit mode
  const handleFieldEdit = (fieldId, newValue) => {
    setEditedResponses(prev => ({
      ...prev,
      [fieldId]: {
        label: prev[fieldId]?.label || fieldId,
        value: newValue
      }
    }));
  };

  // Save edited report with change log
  const saveEditedReport = async () => {
    if (!selectedReport || !auth.currentUser) return;
    
    // Prevent editing locked reports
    if (isReportLocked(selectedReport)) {
      alert('🔒 This report is locked. Approved reports cannot be edited.');
      return;
    }

    try {
      const originalResponses = selectedReport.responses || selectedReport.answers || {};
      const changes = buildSubmissionChangeLog({
        previousResponses: originalResponses,
        nextResponses: editedResponses,
        actor: auth.currentUser,
        platform: "web",
        formDef: selectedReport.formDef || [],
      });

      if (changes.length === 0) {
        alert('No changes detected');
        return;
      }

      // Clean editedResponses to remove undefined values
      const cleanedResponses = {};
      Object.keys(editedResponses).forEach(key => {
        const value = editedResponses[key]?.value;
        cleanedResponses[key] = {
          label: editedResponses[key]?.label || key,
          value: value === undefined ? null : value
        };
      });

      // Update the submission document
      const submissionRef = doc(db, `companies/${selectedReport.companyId}/submissions/${selectedReport.id}`);
      await updateDoc(submissionRef, {
        responses: cleanedResponses,
        changeLog: arrayUnion(...changes),
        lastEditedAt: new Date().toISOString(),
        lastEditedBy: auth.currentUser.email || auth.currentUser.uid,
        ...buildSubmissionEditMetadata(selectedReport, "web"),
      });

      // Update local state
      setSelectedReport(prev => ({
        ...prev,
        responses: cleanedResponses,
        changeLog: [...(prev.changeLog || []), ...changes],
        lastEditedAt: new Date().toISOString(),
        lastEditedBy: auth.currentUser.email || auth.currentUser.uid,
        ...buildSubmissionEditMetadata(prev, "web"),
      }));

      setEditMode(false);
      setEditedResponses(cleanedResponses); // Update with cleaned responses
      alert(`Successfully saved ${changes.length} change(s)`);
    } catch (error) {
      console.error('Failed to save changes:', error);
      alert('Failed to save changes: ' + error.message);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const escapeHtml = (unsafe) => {
    if (unsafe == null) return "";
    return String(unsafe)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  };

  const isImageUrl = (val) => {
    if (!val) return false;
    if (typeof val !== 'string') return false;
    if (val.startsWith('data:image')) return true;
    return /\.(png|jpe?g|gif|bmp|webp)(\?.*)?$/i.test(val) || val.includes('firebasestorage.googleapis.com');
  };

  const formatLocationValue = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (value && typeof value === "object") {
      if (value.location) return value.location;
      if (value.latitude && value.longitude) {
        return `Lat: ${value.latitude}, Lon: ${value.longitude}`;
      }
      try { return JSON.stringify(value); } catch { return String(value); }
    }
    return String(value);
  };

  const printReport = (report) => {
    const container = document.getElementById("premium-report-view");
    if (!container) {
      alert("Report not ready. Open a report first.");
      return;
    }

    const htmlContent = container.innerHTML;

    // Collect evidence thumbnails for gallery
    const evidenceItems = [];
    const imgs = container.querySelectorAll("img");
    imgs.forEach((img) => {
      const label = img.getAttribute("alt") || "Evidence";
      evidenceItems.push({ url: img.src, label });
    });
  const preloadImages = async (rootEl) => {
    const imgs = Array.from(rootEl.querySelectorAll('img'));
    if (imgs.length === 0) return;
    await Promise.all(
      imgs.map((img) =>
        new Promise((resolve) => {
          try {
            img.crossOrigin = 'anonymous';
          } catch {}
          const done = () => resolve(true);
          if (img.complete && img.naturalWidth) return resolve(true);
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
          // safety timeout
          setTimeout(done, 3000);
        })
      )
    );
  };

    const win = window.open("", "_blank");
    if (!win) {
      alert("Please enable popups to export PDF.");
      return;
    }

    win.document.write(`
  <html>
    <head>
      <title>${escapeHtml(report.formTitle || report.title || "Report")}</title>
      <style>
        @page { margin: 18mm; }
        body { font-family: Arial, Helvetica, sans-serif; padding: 10mm; color: #222; }

        /* Minimal Tailwind-like utilities used in PremiumReportRenderer */
        .border{border:1px solid #e5e7eb;} .rounded{border-radius:0.25rem;} .rounded-lg{border-radius:0.5rem;}
        .p-4{padding:1rem;} .p-5{padding:1.25rem;} .px-4{padding-left:1rem;padding-right:1rem;} .py-3{padding-top:0.75rem;padding-bottom:0.75rem;}
        .bg-white{background:#fff;} .bg-gray-50{background:#f9fafb;} .bg-gray-100{background:#f3f4f6;}
        .text-gray-500{color:#6b7280;} .text-gray-600{color:#4b5563;} .text-gray-700{color:#374151;}
        .font-semibold{font-weight:600;} .font-bold{font-weight:700;} .text-lg{font-size:1.125rem;}
        .max-w-sm{max-width:24rem;} .max-w-md{max-width:28rem;}
        .w-20{width:5rem;} .h-20{height:5rem;} .w-40{width:10rem;} .h-40{height:10rem;}
        .object-cover{object-fit:cover;} .cursor-pointer{cursor:pointer;}
        .mt-1{margin-top:0.25rem;} .mt-2{margin-top:0.5rem;} .mt-3{margin-top:0.75rem;} .mb-2{margin-bottom:0.5rem;} .mb-3{margin-bottom:0.75rem;}
        .space-y-8 > * + * { margin-top: 2rem; }
        .shadow-sm{ box-shadow: 0 1px 2px rgba(0,0,0,.05); }
        pre{white-space:pre-wrap;}

        /* Header */
        .header { display:flex; align-items:center; border-bottom:3px solid #333; padding-bottom:10px; margin-bottom:16px; }
        .header img { height:60px; margin-right:20px; }
        .header-title { font-size:26px; font-weight:bold; }
        .header-meta { font-size:12px; color:#555; margin-top:3px; }

        /* Content blocks */
        .field-block, .section-block { border:1px solid #ccc; border-radius:8px; padding:15px; background:#fff; margin-bottom:16px; page-break-inside:avoid; }
        .section-title { font-size:20px; font-weight:bold; margin-bottom:10px; border-bottom:2px solid #eee; padding-bottom:6px; }
        .instance-box { border:1px solid #ddd; padding:12px; border-radius:6px; margin:10px 0; }

        /* Question/Answer separation */
        .question { font-weight: bold; font-size: 14px; margin-bottom: 4px; color: #111; }
        .answer { padding-left: 12px; border-left: 3px solid #e5e7eb; margin-bottom: 12px; font-size: 13px; color: #374151; }
        .answer-text { line-height: 1.5; }
        .pl-3 { padding-left: 0.75rem; }
        .border-l-3 { border-left-width: 3px; }
        .border-l-4 { border-left-width: 4px; }
        .border-gray-200 { border-color: #e5e7eb; }
        .border-gray-300 { border-color: #d1d5db; }

        img.answer-img, img.evidence { max-width:260px; border:1px solid #999; border-radius:5px; margin:6px 0; }

        /* Hide interactive elements in print */
        button { display:none !important; }
        .cursor-pointer { cursor: default !important; }
        
        /* Hide maps in PDF */
        iframe, .map-container { display:none !important; visibility:hidden !important; height:0 !important; }

        /* Evidence gallery */
        .gallery { margin-top:40px; page-break-before:always; }
        .gallery-title { font-size:22px; font-weight:bold; margin-bottom:12px; }
        .gallery-grid { display:grid; grid-template-columns: repeat(3, 1fr); gap:12px; }
        .gallery-item { border:1px solid #ccc; padding:8px; border-radius:6px; background:#fafafa; }
        .gallery-item img { width:100%; border-radius:6px; }
        .gallery-caption { font-size:12px; color:#555; margin-top:4px; text-align:center; }

        /* Footer */
        .footer { position:fixed; bottom:10px; left:0; right:0; text-align:center; font-size:11px; color:#777; }
      </style>
    </head>

    <body>
      <!-- HEADER -->
      <div class="header">
        <img src="${companyLogo || webLogo}" />
        <div>
          <div class="header-title">${escapeHtml(report.formTitle || report.title || "")}</div>
          <div class="header-meta">Report ID: ${escapeHtml(report.id || "")}</div>
          <div class="header-meta">Date: ${escapeHtml(formatDate(report.createdAt || report.submittedAt || report.date || ""))}</div>
          <div class="header-meta">Status: ${escapeHtml(report.status || "submitted")}</div>
        </div>
      </div>

      <!-- WATERMARK (if edited) -->
      ${report.lastEditedAt ? `
        <div style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-20deg);font-size:120px;opacity:0.08;color:#ff0000;pointer-events:none;font-weight:bold;white-space:nowrap;z-index:9999;">
          EDITED AFTER SUBMISSION
        </div>
      ` : ''}

      <!-- MAIN CONTENT -->
      ${htmlContent}

      <!-- REMEDIATION SIGN-OFF -->
      ${report.remediationSignoff?.signed ? `
        <div style="margin-top:30px;padding:20px;background:#d1fae5;border:2px solid #10b981;border-radius:8px;">
          <div style="font-size:18px;font-weight:bold;color:#065f46;margin-bottom:10px;">
            ✔ Remediation Sign-Off
          </div>
          <div style="font-size:14px;color:#047857;margin-bottom:8px;">
            Signed off by <strong>${escapeHtml(report.remediationSignoff.signedBy)}</strong> on 
            <strong>${new Date(report.remediationSignoff.signedAt?.seconds ? report.remediationSignoff.signedAt.seconds * 1000 : report.remediationSignoff.signedAt).toLocaleString()}</strong>
          </div>
          ${report.remediationSignoff.signatureUrl ? `
            <div style="margin-top:12px;">
              <div style="font-size:12px;color:#047857;margin-bottom:4px;">Signature:</div>
              <img src="${report.remediationSignoff.signatureUrl}" alt="Signature" style="max-width:300px;max-height:100px;border:1px solid #10b981;border-radius:4px;background:#fff;padding:8px;" />
            </div>
          ` : ''}
          ${report.remediationSignoff.comment ? `
            <div style="margin-top:12px;font-size:14px;color:#047857;">
              <span style="font-weight:bold;">Comment:</span> ${escapeHtml(report.remediationSignoff.comment)}
            </div>
          ` : ''}
        </div>
      ` : ''}

      <!-- CHANGE LOG APPENDIX -->
      ${report.changeLog && report.changeLog.length > 0 ? `
        <div style="page-break-before:always;margin-top:40px;">
          <h2 style="font-size:22px;font-weight:bold;border-bottom:3px solid #333;padding-bottom:10px;margin-bottom:20px;">📋 Change History</h2>
          
          <div style="background:#fffbea;border:2px solid #f59e0b;padding:15px;border-radius:8px;margin-bottom:20px;">
            <strong style="font-size:16px;">🧠 Summary:</strong>
            <div style="margin-top:8px;font-size:14px;">
              ${(() => {
                const summary = (() => {
                  const uniqueFields = new Set(report.changeLog.map(c => c.field));
                  const uniqueEditors = new Set(report.changeLog.map(c => c.changedBy));
                  return `${report.changeLog.length} field${report.changeLog.length > 1 ? 's' : ''} modified post-submission by ${uniqueEditors.size} user${uniqueEditors.size > 1 ? 's' : ''}`;
                })();
                return summary;
              })()}
            </div>
          </div>
          
          <table style="width:100%;border-collapse:collapse;font-size:12px;">
            <thead style="background:#f3f4f6;">
              <tr>
                <th style="border:1px solid #ddd;padding:10px;text-align:left;">Field</th>
                <th style="border:1px solid #ddd;padding:10px;text-align:left;">Old Value</th>
                <th style="border:1px solid #ddd;padding:10px;text-align:left;">New Value</th>
                <th style="border:1px solid #ddd;padding:10px;text-align:left;">Changed By</th>
                <th style="border:1px solid #ddd;padding:10px;text-align:left;">Date</th>
              </tr>
            </thead>
            <tbody>
              ${report.changeLog.map(c => `
                <tr>
                  <td style="border:1px solid #ddd;padding:8px;font-weight:600;">${escapeHtml(c.field)}</td>
                  <td style="border:1px solid #ddd;padding:8px;background:#fee;">
                    ${escapeHtml(typeof c.oldValue === 'object' ? JSON.stringify(c.oldValue) : String(c.oldValue || '(empty)'))}
                  </td>
                  <td style="border:1px solid #ddd;padding:8px;background:#efe;">
                    ${escapeHtml(typeof c.newValue === 'object' ? JSON.stringify(c.newValue) : String(c.newValue || '(empty)'))}
                  </td>
                  <td style="border:1px solid #ddd;padding:8px;">${escapeHtml(c.changedBy)}</td>
                  <td style="border:1px solid #ddd;padding:8px;">${new Date(c.changedAt).toLocaleString()}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      ` : ''}

      <!-- EVIDENCE GALLERY -->
      <div class="gallery">
        <div class="gallery-title">Evidence Gallery</div>
        <div class="gallery-grid">
          ${evidenceItems
            .map(
              (e) => `
            <div class="gallery-item">
              <img src="${e.url}" />
              <div class="gallery-caption">${escapeHtml(e.label)}</div>
            </div>`
            )
            .join("")}
        </div>
      </div>

      <div class="footer">
        BuildMyChecks • Enterprise PDF Report • Page <span class="pageNumber"></span>
      </div>

      <script>
        window.onload = function () {
          // Remove empty remediation blocks (only header, no notes or attachments)
          document.querySelectorAll('.bg-yellow-50').forEach(function(block) {
            // Check if it's a remediation block by looking for the header
            if (block.textContent.includes('⚠️ Remediation')) {
              // Count actual content: notes and attachments (not buttons)
              const notes = block.querySelectorAll('.bg-white.rounded.border-yellow-200');
              const attachments = block.querySelectorAll('img, a[href]');
              const hasContent = notes.length > 0 || attachments.length > 0;
              
              // If no content, remove the entire block
              if (!hasContent) {
                block.remove();
              }
            }
          });
          
          document.querySelectorAll('.pageNumber').forEach((el, idx) => {
            el.textContent = idx + 1;
          });
          setTimeout(() => window.print(), 700);
        };
      </script>

    </body>
  </html>
    `);

    win.document.close();
  };

  const formatDate = (val) => {
    if (!val) return "";
    // Firestore Timestamp
    if (typeof val.toDate === "function") return val.toDate().toLocaleString();
    // Object with seconds
    if (val.seconds) return new Date(val.seconds * 1000).toLocaleString();
    const d = new Date(val);
    if (!isNaN(d)) return d.toLocaleString();
    return String(val);
  };

  const getTitle = (doc) => {
    if (!doc) return "";
    if (doc.title) return doc.title;
    if (doc.formTitle) return doc.formTitle;
    if (doc.formName) return doc.formName;
    if (doc.name) return doc.name;
    if (doc.checkTitle) return doc.checkTitle;
    // If fields array present, return first field label/value combo
    if (Array.isArray(doc.fields) && doc.fields.length) {
      const f = doc.fields[0];
      return `${f.label || 'Field'}: ${f.value || ''}`;
    }
    // Try common nested properties
    if (doc.submitted && typeof doc.submitted === 'string') return doc.submitted;
    return `Submission ${doc.id || ''}`;
  };

  const renderAnswers = (answers) => {
    if (!answers || typeof answers !== 'object') return null;
    return Object.entries(answers).map(([qid, val]) => {
      // support {label, value} shape
      let label = qid;
      let value = val;
      if (val && typeof val === 'object' && ('label' in val || 'value' in val)) {
        label = val.label || qid;
        value = val.value;
      }
      const isImg = isImageUrl(String(value || ''));
      return (
        <li key={qid} className="mb-3">
          <div className="font-medium">{escapeHtml(label)}</div>
          <div className="mt-1">
            {isImg ? (
              <img src={String(value)} alt={label} style={{ maxWidth: 320, borderRadius: 6 }} />
            ) : (
              <div className="text-sm">{formatLocationValue(value ?? '')}</div>
            )}
          </div>
        </li>
      );
    });
  };

  const getStatus = (doc) => {
    if (!doc) return "";
    if (doc.status) return doc.status;
    if (doc.completed) return doc.completed === true ? "Completed" : String(doc.completed);
    return "";
  };

  // Build a map of searchable field IDs per form for fast lookup
  const searchableFieldsMap = useMemo(() => {
    const map = {};
    forms.forEach((form) => {
      const ids = new Set();
      const walk = (fields) => {
        (fields || []).forEach((f) => {
          if (f.config?.searchable) ids.add(f.id);
          if (Array.isArray(f.fields)) walk(f.fields);
          if (Array.isArray(f.children)) walk(f.children);
        });
      };
      walk(form.fields);
      if (ids.size > 0) map[form.id] = ids;
    });
    return map;
  }, [forms]);

  // Extract a plain text string from a response value for searching
  const getSearchableText = (val) => {
    if (val == null) return "";
    if (typeof val === "string") return val;
    if (typeof val === "boolean") return val ? "yes" : "no";
    if (typeof val === "number") return String(val);
    if (typeof val === "object") {
      if (val.answer !== undefined) return String(val.answer);
      if (val.value !== undefined) return getSearchableText(val.value);
      if (Array.isArray(val)) return val.map(getSearchableText).join(" ");
    }
    return String(val);
  };

  const filteredChecks = completedChecks.filter((check) => {
    const title = getTitle(check).toLowerCase();
    const q = search.toLowerCase();
    let matchSearch =
      title.includes(q) ||
      (check.id || "").toLowerCase().includes(q) ||
      (check.status || "").toLowerCase().includes(q);

    // Also search values of fields marked as searchable
    if (!matchSearch && q && check.formId) {
      const searchableIds = searchableFieldsMap[check.formId];
      if (searchableIds) {
        const responses = check.responses || check.answers || {};
        for (const fieldId of searchableIds) {
          const resp = responses[fieldId];
          if (!resp) continue;
          const raw = resp?.value !== undefined ? resp.value : resp;
          const text = getSearchableText(raw).toLowerCase();
          if (text.includes(q)) { matchSearch = true; break; }
        }
      }
    }
    const matchStatus = filterStatus === "all" ? true : (check.status || "") === filterStatus;
    const matchForm = filterFormId === "all" ? true : (check.formId || "") === filterFormId;
    // Date range filter
    const created = check.createdAt || check.submittedAt || check.date;
    let matchDate = true;
    try {
      const createdMs = (typeof created?.toDate === "function") ? created.toDate().getTime() : created?.seconds ? created.seconds * 1000 : new Date(created).getTime();
      if (dateFrom) {
        const fromMs = new Date(dateFrom).getTime();
        if (!isNaN(fromMs)) matchDate = matchDate && createdMs >= fromMs;
      }
      if (dateTo) {
        const toMs = new Date(dateTo).getTime();
        if (!isNaN(toMs)) matchDate = matchDate && createdMs <= toMs + 24*60*60*1000 - 1; // inclusive end of day
      }
    } catch {}
    return matchSearch && matchStatus && matchForm && matchDate;
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredChecks.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedChecks = filteredChecks.slice((currentPage - 1) * pageSize, (currentPage - 1 + 1) * pageSize);
  const uniqueForms = Array.from(new Set(completedChecks.map(c => c.formId).filter(Boolean)));

  const selectedPreview = (doc) => {
    if (!doc) return '';
    if (doc.answers && typeof doc.answers === 'object') {
      const entries = Object.entries(doc.answers);
      if (!entries.length) return '';
      const [qid, val] = entries[0];
      const display = Array.isArray(val) ? val.slice(0,3).join(', ') : (val && typeof val === 'object' ? JSON.stringify(val) : String(val));
      return `${qid}: ${display}`;
    }
    if (Array.isArray(doc.fields) && doc.fields.length) {
      return doc.fields.slice(0,3).map(f => `${f.label}: ${String(f.value)}`).join(' • ');
    }
    try {
      return JSON.stringify(doc).slice(0,80);
    } catch (e) {
      return '';
    }
  };

  const renderReportHTML = (report, formDef, meta) => {
    const component = (
      <PremiumReportRenderer 
        formDef={formDef || []} 
        responses={report.responses || report.answers || {}} 
        changeLog={report.changeLog || []}
        remediation={report.remediation || {}}
        readOnly={true}
      />
    );
    const content = renderToStaticMarkup(component);
    const title = escapeHtml(meta?.title || getTitle(report) || "Report");
    const dateStr = escapeHtml(formatDate(meta?.date || report.createdAt || report.submittedAt || report.date || ""));
    const statusStr = escapeHtml(meta?.status || report.status || "submitted");
    const idStr = escapeHtml(report.id || "");
    // Wrap with header + refreshed styles for PDF output
    return `
      <style>
        @page { margin: 12mm; }
        body { font-family: 'Inter', Arial, sans-serif; padding: 10mm; color: #0f172a; background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%); }

        /* Minimal Tailwind-like utilities used in PremiumReportRenderer */
        .border{border:1px solid #e5e7eb;} .rounded{border-radius:0.25rem;} .rounded-lg{border-radius:0.5rem;}
        .p-4{padding:1rem;} .p-5{padding:1.25rem;} .px-4{padding-left:1rem;padding-right:1rem;} .py-3{padding-top:0.75rem;padding-bottom:0.75rem;}
        .bg-white{background:#fff;} .bg-gray-50{background:#f9fafb;} .bg-gray-100{background:#f3f4f6;}
        .text-gray-500{color:#6b7280;} .text-gray-600{color:#4b5563;} .text-gray-700{color:#374151;}
        .font-semibold{font-weight:600;} .font-bold{font-weight:700;} .text-lg{font-size:1.125rem;}
        .max-w-sm{max-width:24rem;} .max-w-md{max-width:28rem;}
        .w-20{width:5rem;} .h-20{height:5rem;} .w-40{width:10rem;} .h-40{height:10rem;}
        .object-cover{object-fit:cover;} .cursor-pointer{cursor:pointer;}
        .mt-1{margin-top:0.25rem;} .mt-2{margin-top:0.5rem;} .mt-3{margin-top:0.75rem;} .mb-2{margin-bottom:0.5rem;} .mb-3{margin-bottom:0.75rem;}
        .space-y-8 > * + * { margin-top: 2rem; }
        .shadow-sm{ box-shadow: 0 1px 2px rgba(0,0,0,.05); }
        pre{white-space:pre-wrap;}

        /* Shell */
        .pdf-shell { background:#ffffff; border:1px solid #e5e7eb; border-radius:16px; padding:22px; box-shadow: 0 12px 40px rgba(15,23,42,0.12); position:relative; }

        /* Header */
        .header { display:flex; align-items:flex-start; gap:16px; border-bottom:2px solid #e2e8f0; padding-bottom:14px; margin-bottom:18px; }
        .logo-chip { width:64px; height:64px; border-radius:18px; background:linear-gradient(135deg,#0f172a,#1e293b); display:flex; align-items:center; justify-content:center; box-shadow:0 10px 30px rgba(15,23,42,0.18); }
        .logo-chip img { height:40px; width:auto; filter: drop-shadow(0 4px 10px rgba(0,0,0,0.15)); }
        .header-title { font-size:26px; font-weight:800; letter-spacing:-0.02em; color:#0f172a; margin:0; }
        .eyebrow { text-transform:uppercase; letter-spacing:0.18em; font-size:11px; color:#64748b; font-weight:700; margin-bottom:6px; }
        .meta-row { display:flex; flex-wrap:wrap; gap:8px; margin-top:8px; }
        .meta-chip { background:#f1f5f9; color:#0f172a; border:1px solid #e2e8f0; border-radius:999px; padding:6px 10px; font-size:11px; font-weight:600; }
        .status-pill { padding:6px 12px; border-radius:999px; font-weight:700; font-size:12px; text-transform:capitalize; border:1px solid transparent; }
        .status-submitted { background:#eef2ff; color:#4338ca; border-color:#c7d2fe; }
        .status-approved { background:#ecfdf3; color:#15803d; border-color:#bbf7d0; }
        .status-failed { background:#fef2f2; color:#b91c1c; border-color:#fecdd3; }
        .status-remediated { background:#f0fdf4; color:#166534; border-color:#bbf7d0; }

        /* Content blocks */
        .field-block, .section-block { border:1px solid #e2e8f0; border-radius:12px; padding:14px 16px; background:#ffffff; margin-bottom:12px; page-break-inside:avoid; box-shadow:0 6px 16px rgba(15,23,42,0.06); }
        .section-title { font-size:18px; font-weight:800; margin-bottom:10px; border-bottom:2px solid #e2e8f0; padding-bottom:6px; color:#0f172a; letter-spacing:-0.01em; }
        .instance-box { border:1px dashed #cbd5e1; padding:12px; border-radius:10px; margin:10px 0; background:linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%); }

        /* Question/Answer separation */
        .question { font-weight: 700; font-size: 14px; margin-bottom: 4px; color: #0f172a; }
        .answer { padding-left: 12px; border-left: 3px solid #cbd5e1; margin-bottom: 10px; font-size: 13px; color: #334155; line-height:1.5; }
        .answer-text { line-height: 1.5; }
        .pl-3 { padding-left: 0.75rem; }
        .border-l-3 { border-left-width: 3px; }
        .border-l-4 { border-left-width: 4px; }
        .border-gray-200 { border-color: #e5e7eb; }
        .border-gray-300 { border-color: #d1d5db; }

        img.answer-img, img.evidence { max-width:260px; border:1px solid #cbd5e1; border-radius:10px; margin:6px 0; box-shadow:0 8px 24px rgba(15,23,42,0.12); }

        /* Body layout */
        .report-body { display:flex; flex-direction:column; gap:12px; }

        /* Footer */
        .footer { position:fixed; bottom:8mm; left:0; right:0; text-align:center; color:#94a3b8; font-size:10px; letter-spacing:0.08em; }

        /* Hide maps in PDF */
        iframe, .map-container { display:none !important; visibility:hidden !important; height:0 !important; }
      </style>
      <div class="pdf-shell">
        <div class="header">
          <div class="logo-chip">
            <img src="${companyLogo || webLogo}" />
          </div>
          <div>
            <div class="eyebrow">BuildMyChecks • Completed Check</div>
            <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
              <div class="header-title">${title}</div>
              <span class="status-pill status-${statusStr.toLowerCase()}">${statusStr}</span>
            </div>
            <div class="meta-row">
              <span class="meta-chip">Report ID: ${idStr}</span>
              <span class="meta-chip">Date: ${dateStr}</span>
            </div>
          </div>
        </div>
        <div class="report-body">${content}</div>
        <div class="footer">BuildMyChecks • Confidential • ${dateStr}</div>
      </div>
    `;
  };

  const generatePDFBlob = async (element) => {
    // small delay allows images/fonts to settle before capture
    await new Promise((r) => setTimeout(r, 150));
    const proxy = process.env.REACT_APP_HTML2CANVAS_PROXY || undefined;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      imageTimeout: 5000,
      proxy,
      removeContainer: true,
      backgroundColor: '#ffffff'
    });
    let imgData;
    try {
      imgData = canvas.toDataURL("image/jpeg", 0.95);
    } catch (e) {
      imgData = canvas.toDataURL("image/png");
    }
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;

    // Precise slice-based pagination: cut the canvas into page-high chunks
    const pxPerMm = canvas.width / pageWidth; // scale relation
    const sliceHeightPx = Math.floor(pageHeight * pxPerMm);

    const totalPages = Math.ceil(canvas.height / sliceHeightPx);
    for (let page = 0; page < totalPages; page++) {
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      const currentSliceHeight = Math.min(sliceHeightPx, canvas.height - page * sliceHeightPx);
      sliceCanvas.height = currentSliceHeight;
      const ctx = sliceCanvas.getContext('2d');
      ctx.drawImage(
        canvas,
        0,
        page * sliceHeightPx,
        canvas.width,
        currentSliceHeight,
        0,
        0,
        canvas.width,
        currentSliceHeight
      );
      const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.95);
      const sliceHeightMm = (currentSliceHeight / pxPerMm);
      if (page > 0) pdf.addPage();
      pdf.addImage(sliceData, "JPEG", 0, 0, imgWidth, sliceHeightMm);
    }
    return pdf.output("blob");
  };

  const downloadZip = async () => {
    if (selectedIds.length === 0) return;
    const zip = new JSZip();
    for (const reportId of selectedIds) {
      const report = completedChecks.find((c) => c.id === reportId);
      if (!report) continue;
      let formDef = [];
      try {
        if (report.companyId && report.formId) {
          const formSnap = await getDoc(doc(db, `companies/${report.companyId}/forms/${report.formId}`));
          if (formSnap.exists()) {
            formDef = normalizeFields(formSnap.data().fields);
          }
        }
      } catch {}
      // Server-side PDF render via Cloud Function if configured
      const funcUrl = process.env.REACT_APP_PDF_FUNCTION_URL;
      const filename = getPdfFilename(report);
      try {
        if (funcUrl) {
          const html = renderReportHTML(report, formDef, { title: getTitle(report), date: report.createdAt || report.submittedAt || report.date, status: report.status });
          const resp = await fetch(funcUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/pdf' },
            body: JSON.stringify({ html, title: filename.replace(/\.pdf$/i, '') })
          });
          if (!resp.ok) {
            const errText = await resp.text().catch(() => '');
            console.error('PDF function error', resp.status, errText);
            throw new Error('PDF function error');
          }
          const blob = await resp.blob();
          console.log('[ZIP] Received blob, size:', blob.size);
          if (!blob || blob.size < 1024) {
            console.warn('[ZIP] Received unusually small PDF blob; falling back to client capture.', blob.size);
            throw new Error('Small PDF');
          }
          zip.file(filename, blob);
          console.log('[ZIP] Added to ZIP successfully');
          continue;
        }
      } catch (e) {
        console.warn('[ZIP] Server-side PDF failed, using client fallback:', e.message);
      }

      // Fallback: client-side capture if function not set or failed
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'fixed';
      tempDiv.style.left = '-10000px';
      tempDiv.style.top = '-10000px';
      tempDiv.innerHTML = `<div id="pdf-render" style="width:794px">${renderReportHTML(report, formDef, { title: getTitle(report), date: report.createdAt || report.submittedAt || report.date, status: report.status })}</div>`;
      document.body.appendChild(tempDiv);
      {
        const imgs = Array.from(tempDiv.querySelectorAll('img'));
        if (imgs.length) {
          await Promise.all(imgs.map((img) => new Promise((resolve) => {
            try { img.crossOrigin = 'anonymous'; } catch {}
            const done = () => resolve(true);
            if (img.complete && img.naturalWidth) return resolve(true);
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
            setTimeout(done, 3000);
          })));
        }
      }
      try {
        const pdfBlob = await generatePDFBlob(tempDiv.querySelector('#pdf-render'));
        zip.file(filename, pdfBlob);
      } catch {}
      document.body.removeChild(tempDiv);
    }
    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(zipBlob, `reports_${Date.now()}.zip`);
  };

  const exportSelected = async () => {
    if (selectedIds.length === 0) return;
    if (selectedIds.length > 5) {
      await downloadZip();
      return;
    }
    for (const reportId of selectedIds) {
      const report = completedChecks.find((c) => c.id === reportId);
      if (!report) continue;
      let formDef = [];
      try {
        if (report.companyId && report.formId) {
          const formSnap = await getDoc(doc(db, `companies/${report.companyId}/forms/${report.formId}`));
          if (formSnap.exists()) formDef = normalizeFields(formSnap.data().fields);
        }
      } catch {}
      const funcUrl = process.env.REACT_APP_PDF_FUNCTION_URL;
      const filename = getPdfFilename(report);
      console.log('[MULTI] Processing report:', report.id, 'filename:', filename, 'funcUrl:', funcUrl ? 'SET' : 'NOT SET');
      try {
        if (funcUrl) {
          const html = renderReportHTML(report, formDef, { title: getTitle(report), date: report.createdAt || report.submittedAt || report.date, status: report.status });
          console.log('[ZIP] Sending to function, HTML length:', html.length, 'Has CSS?', html.includes('.field-block'));
          const resp = await fetch(funcUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/pdf' },
            body: JSON.stringify({ html, title: filename.replace(/\.pdf$/i, '') })
          });
          if (!resp.ok) {
            const errText = await resp.text().catch(() => '');
            console.error('PDF function error', resp.status, errText);
            throw new Error('PDF function error');
          }
          const blob = await resp.blob();
          console.log('[MULTI] Received blob, size:', blob.size);
          if (!blob || blob.size < 1024) {
            console.warn('[MULTI] Received unusually small PDF blob; falling back to client capture.', blob.size);
            throw new Error('Small PDF');
          }
          saveAs(blob, filename);
          console.log('[MULTI] Download triggered successfully');
          continue;
        }
      } catch (e) {
        console.warn('[MULTI] Server-side PDF failed, using client fallback:', e.message);
      }

      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'fixed';
      tempDiv.style.left = '-10000px';
      tempDiv.style.top = '-10000px';
      tempDiv.innerHTML = `<div id="pdf-render" style="width:794px">${renderReportHTML(report, formDef, { title: getTitle(report), date: report.createdAt || report.submittedAt || report.date, status: report.status })}</div>`;
      document.body.appendChild(tempDiv);
      {
        const imgs = Array.from(tempDiv.querySelectorAll('img'));
        if (imgs.length) {
          await Promise.all(imgs.map((img) => new Promise((resolve) => {
            try { img.crossOrigin = 'anonymous'; } catch {}
            const done = () => resolve(true);
            if (img.complete && img.naturalWidth) return resolve(true);
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
            setTimeout(done, 3000);
          })));
        }
      }
      try {
        const pdfBlob = await generatePDFBlob(tempDiv.querySelector('#pdf-render'));
        saveAs(pdfBlob, filename);
      } catch {}
      document.body.removeChild(tempDiv);
    }
  };

  const getPdfFilename = (report) => {
    const title = (getTitle(report) || "Report").replace(/[^a-z0-9]+/gi, "_");
    const dateStr = (formatDate(report.createdAt || report.submittedAt || report.date || "") || "").replace(/[^0-9]+/g, "-").slice(0, 10);
    return `${title}_${dateStr}.pdf`;
  };

  const loadFullReportIntoContainer = async (submission) => {
    const container = document.getElementById("hidden-report-pdf-container");
    if (!container) return null;
    let formDef = [];
    try {
      if (submission.companyId && submission.formId) {
        const formSnap = await getDoc(doc(db, `companies/${submission.companyId}/forms/${submission.formId}`));
        if (formSnap.exists()) {
          formDef = normalizeFields(formSnap.data().fields);
        }
      }
    } catch {}
    const html = renderReportHTML({ responses: submission.responses || submission.answers || {} }, formDef);
    container.innerHTML = `<div id="internal-render">${html}</div>`;
    return container.innerHTML;
  };

  const printHTML = (html, filename) => {
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`
      <html>
        <head><title>${filename}</title></head>
        <body>${html}</body>
      </html>
    `);
    win.document.close();
    setTimeout(() => { win.print(); win.close(); }, 600);
  };

  // Combined PDF Generator - merges all selected reports into ONE PDF
  const downloadCombinedPDF = async () => {
    if (selectedIds.length === 0) {
      alert("Please select reports to download");
      return;
    }

    console.log(`[COMBINED] Building merged PDF for ${selectedIds.length} reports...`);
    
    // Build wrapper container
    const wrapper = document.createElement("div");
    wrapper.id = "combined-pdf-wrapper";
    wrapper.style.padding = "20px";
    wrapper.style.width = "800px";
    wrapper.style.background = "#ffffff";
    wrapper.style.position = "absolute";
    wrapper.style.left = "-9999px";
    wrapper.style.fontFamily = "Arial, sans-serif";
    document.body.appendChild(wrapper);

    // Insert each report into the wrapper
    for (let index = 0; index < selectedIds.length; index++) {
      const reportId = selectedIds[index];
      const report = completedChecks.find(c => c.id === reportId);
      if (!report) continue;

      // Fetch form definition for this report
      let formDef = [];
      try {
        if (report.companyId && report.formId) {
          const formSnap = await getDoc(doc(db, `companies/${report.companyId}/forms/${report.formId}`));
          if (formSnap.exists()) {
            formDef = normalizeFields(formSnap.data().fields);
          }
        }
      } catch (err) {
        console.warn(`Could not load form for ${reportId}:`, err);
      }

      // Render the report HTML
      const html = renderReportHTML({ responses: report.responses || report.answers || {} }, formDef);
      
      const page = document.createElement("div");
      page.className = "pdf-page";
      page.style.pageBreakAfter = "always";
      page.style.marginBottom = "40px";

      page.innerHTML = `
        <div class="pdf-header" style="display:flex;align-items:center;gap:15px;margin-bottom:20px;border-bottom:2px solid #e5e7eb;padding-bottom:10px;">
          <img src="${companyLogo || webLogo}" style="height:40px;object-fit:contain;" crossorigin="anonymous" />
          <div>
            <h2 style="margin:0;font-size:18px;font-weight:600;">
              Report ${index + 1} of ${selectedIds.length}
            </h2>
            <div style="font-size:14px;color:#666;margin-top:4px;">
              ${getTitle(report)} - ${formatDate(report.createdAt || report.date || report.submittedAt)}
            </div>
          </div>
        </div>
        <div>${html}</div>
      `;

      wrapper.appendChild(page);
    }

    // Convert wrapper into a PDF
    const opt = {
      margin: [10, 10, 10, 10],
      filename: `combined_reports_${new Date().toISOString().split('T')[0]}.pdf`,
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait"
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    try {
      await html2pdf().set(opt).from(wrapper).save();
      console.log("[COMBINED] PDF generated successfully");
    } catch (err) {
      console.error("[COMBINED] PDF generation failed:", err);
      alert("Failed to generate combined PDF: " + err.message);
    } finally {
      wrapper.remove();
    }
  };

  // Print View - opens reports in new tab for browser print
  const handlePrintSelected = async () => {
    if (selectedIds.length === 0) {
      alert("Please select reports to print");
      return;
    }

    const companyId = await getCurrentUserCompanyId();
    const idString = selectedIds.join(",");
    const logoUrl = encodeURIComponent(companyLogo || webLogo);

    window.open(
      `/print?ids=${idString}&company=${companyId}&logo=${logoUrl}`,
      "_blank"
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-100 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-32 -top-24 w-80 h-80 bg-rose-600/20 blur-3xl rounded-full" />
        <div className="absolute right-8 top-20 w-96 h-96 bg-amber-300/12 blur-3xl rounded-full" />
        <div className="absolute -right-32 bottom-10 w-96 h-96 bg-rose-500/12 blur-3xl rounded-full" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <img src={webLogo} alt="Web Logo" className="h-12 w-auto" />
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-gray-400">{t('reports.eyebrow')}</p>
              <h1 className="text-3xl font-bold text-white">{t('reports.completedChecks')}</h1>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              className="px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-gray-100 hover:bg-white/15 transition"
              onClick={() => window.location.href = "https://buildmychecks.com/home"}
            >
              {t('dashboardHeader.home')}
            </button>
            <button
              className="px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-gray-100 hover:bg-white/20 transition"
              onClick={() => navigate(-1)}
            >
              {t('common.back')}
            </button>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-3xl shadow-2xl p-6 lg:p-8">
          <div id="hidden-report-pdf-container" style={{ display: "none" }}></div>
          {loading ? (
            <div className="text-gray-200">{t('reports.loadingSubmissions')}</div>
          ) : error ? (
            <div className="text-rose-300">{error}</div>
          ) : selectedReport ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-semibold text-white">{getTitle(selectedReport)}</h2>
                <span className="text-xs text-gray-300">{t('reports.idLabel')}{selectedReport.id}</span>
              </div>
              <div className="text-sm text-gray-300">{t('reports.dateLabel')}{formatDate(selectedReport.createdAt || selectedReport.date || selectedReport.submittedAt)}</div>
              <div className="text-sm text-gray-300">{t('reports.statusLabel')}{getStatus(selectedReport)}</div>

              {isReportLocked(selectedReport) && (
                <div className="mb-2 px-4 py-2 bg-rose-500/15 border border-rose-300/40 rounded flex items-center gap-2 text-rose-100">
                  <span className="text-2xl">🔒</span>
                  <strong>{t('reports.lockedLabel')}</strong> {t('reports.lockedExplanation')}
                </div>
              )}

              {selectedReport.lastEditedAt && !editMode && (
                <div className="mb-2 px-4 py-2 bg-amber-400/15 border border-amber-300/40 rounded flex items-center gap-2 text-amber-100">
                  <span className="text-2xl">🕒</span>
                  <div>
                    <strong>Edited After Submission</strong>
                    <div className="text-xs text-amber-100/80">
                      Last edited by {selectedReport.lastEditedBy} on {new Date(selectedReport.lastEditedAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              )}

              {selectedReport.changeLog && selectedReport.changeLog.length > 0 && !editMode && (() => {
                const summary = generateChangeSummary(selectedReport.changeLog);
                return (
                  <div className="mb-2 px-4 py-3 bg-blue-500/10 border border-blue-300/30 rounded">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">🧠</span>
                      <strong className="text-lg text-white">{t('reports.aiSummary')}</strong>
                    </div>
                    <div className="text-sm text-gray-200 space-y-1">
                      <div>{summary.summary}</div>
                      <div>• {summary.uniqueFields} unique field{summary.uniqueFields > 1 ? 's' : ''} modified</div>
                      <div>• Most edited: {summary.mostEdited}</div>
                    </div>
                  </div>
                );
              })()}

              {editMode && (
                <div className="mb-2 px-4 py-2 bg-amber-400/15 border border-amber-300/40 rounded text-amber-100">
                  <strong>{t('reports.editModeLabel')}</strong> {t('reports.editModeInstruction')}
                </div>
              )}

              {selectedReport.changeLog && selectedReport.changeLog.length > 0 && (
                <div className="mb-3">
                  <button
                    className="text-blue-200 hover:text-white text-sm font-medium"
                    onClick={() => setShowChangeLog(!showChangeLog)}
                  >
                    {showChangeLog ? '▼' : '▶'} Change History ({selectedReport.changeLog.length} edit{selectedReport.changeLog.length > 1 ? 's' : ''})
                  </button>

                  {showChangeLog && (
                    <div className="mt-2 border border-white/10 rounded p-3 bg-white/5 max-h-64 overflow-y-auto text-gray-100">
                      {selectedReport.changeLog.map((change, idx) => (
                        <div key={idx} className="mb-3 pb-3 border-b border-white/10 last:border-b-0">
                          <div className="text-sm font-semibold text-white">{change.field}</div>
                          <div className="text-xs text-gray-300 mt-1">
                            Changed by {change.changedBy} on {new Date(change.changedAt).toLocaleString()}
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <div className="font-medium text-rose-200">Old Value:</div>
                              <div className="bg-rose-500/10 border border-rose-300/30 p-2 rounded break-words text-gray-100">
                                {typeof change.oldValue === 'object' ? JSON.stringify(change.oldValue) : String(change.oldValue || '(empty)')}
                              </div>
                            </div>
                            <div>
                              <div className="font-medium text-emerald-200">New Value:</div>
                              <div className="bg-emerald-500/10 border border-emerald-300/30 p-2 rounded break-words text-gray-100">
                                {typeof change.newValue === 'object' ? JSON.stringify(change.newValue) : String(change.newValue || '(empty)')}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mb-4">
                <h3 className="font-semibold mb-2 text-white">{t('reports.submittedFields')}</h3>
                {loadingReport ? (
                  <div className="text-gray-300">{t('reports.loadingFormLayout')}</div>
                ) : editMode ? (
                  <div className="space-y-4 text-gray-900">
                  {visibleReportFields.map((field) => {
                    const currentValue = editedResponses[field.id]?.value;
                    return (
                      <div key={field.id} className="border rounded p-4 bg-white">
                        <label className="block font-semibold mb-2">{field.label || field.question}</label>
                        {field.type === 'text' || field.type === 'number' ? (
                          <input
                            type={field.type === 'number' ? 'number' : 'text'}
                            value={currentValue || ''}
                            onChange={(e) => handleFieldEdit(field.id, e.target.value)}
                            className="w-full px-3 py-2 border rounded"
                          />
                        ) : field.type === 'slider' ? (
                          <div>
                            <input
                              type="range"
                              min={field.config?.min || 0}
                              max={field.config?.max || 10}
                              step={field.config?.step || 1}
                              value={currentValue || 0}
                              onChange={(e) => handleFieldEdit(field.id, Number(e.target.value))}
                              className="w-full"
                            />
                            <div className="text-sm text-gray-600 mt-1">Value: {currentValue}</div>
                          </div>
                        ) : field.type === 'yesno' ? (
                          <select
                            value={currentValue || ''}
                            onChange={(e) => handleFieldEdit(field.id, e.target.value)}
                            className="w-full px-3 py-2 border rounded"
                          >
                            <option value="">Select...</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        ) : field.type === 'checkbox' ? (
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={currentValue === true || currentValue === 'true'}
                              onChange={(e) => handleFieldEdit(field.id, e.target.checked)}
                              className="w-5 h-5"
                            />
                            <span className="text-sm">Checked</span>
                          </label>
                        ) : field.type === 'date_time' || field.type === 'date' ? (
                          <input
                            type="date"
                            value={currentValue || ''}
                            onChange={(e) => handleFieldEdit(field.id, e.target.value)}
                            className="w-full px-3 py-2 border rounded"
                          />
                        ) : field.type === 'multiple_choice' && field.config?.options ? (
                          <select
                            value={currentValue || ''}
                            onChange={(e) => handleFieldEdit(field.id, e.target.value)}
                            className="w-full px-3 py-2 border rounded"
                          >
                            <option value="">Select...</option>
                            {field.config.options.map((opt, idx) => (
                              <option key={idx} value={opt.label || opt}>
                                {opt.label || opt}
                              </option>
                            ))}
                          </select>
                        ) : field.type === 'document_number' ? (
                          <input
                            type="text"
                            value={currentValue || ''}
                            onChange={(e) => handleFieldEdit(field.id, e.target.value)}
                            className="w-full px-3 py-2 border rounded bg-gray-50"
                            placeholder="Document number"
                          />
                        ) : field.type === 'instruction' ? (
                          <div className="text-sm text-gray-500 italic bg-blue-50 p-3 rounded">
                            Instruction blocks cannot be edited
                          </div>
                        ) : field.type === 'asset' ? (
                          <div className="bg-gray-50 p-3 rounded border">
                            <div className="font-semibold text-gray-900">{resolveAssetName(currentValue)}</div>
                            {currentValue?.type && (
                              <div className="text-sm text-gray-600">Type: {currentValue.type}</div>
                            )}
                            {currentValue?.role && (
                              <div className="text-sm text-gray-600">Role: {currentValue.role}</div>
                            )}
                            {currentValue?.serial && (
                              <div className="text-sm text-gray-600">Serial: {currentValue.serial}</div>
                            )}
                            {currentValue?.location && (
                              <div className="text-sm text-gray-600">Location: {currentValue.location}</div>
                            )}
                            {currentValue?.category && (
                              <div className="text-sm text-gray-600">Category: {currentValue.category}</div>
                            )}
                            {currentValue?.mainPhotoURL && (
                              <div className="mt-2">
                                <img
                                  src={currentValue.mainPhotoURL}
                                  alt={resolveAssetName(currentValue)}
                                  className="w-full max-h-56 object-contain rounded border"
                                />
                              </div>
                            )}
                            {Array.isArray(currentValue?.fields) && currentValue.fields.length > 0 && (
                              <div className="mt-3 space-y-1">
                                <div className="text-sm font-semibold text-gray-800">Asset Fields</div>
                                {currentValue.fields.map((assetField, idx) => (
                                  <div key={assetField.id || idx} className="text-xs text-gray-600">
                                    <strong>{assetField.label}:</strong> {assetField.value || "—"}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="mt-3">
                              <label className="text-sm font-semibold text-gray-700 mb-1 block">
                                Admin Note (asset incorrect, damaged, etc):
                              </label>
                              <textarea
                                placeholder="Add note about this asset..."
                                className="w-full border rounded p-2 text-sm"
                                value={currentValue?.adminNote || ''}
                                onChange={(e) =>
                                  handleFieldEdit(field.id, {
                                    ...currentValue,
                                    adminNote: e.target.value
                                  })
                                }
                                rows={2}
                              />
                            </div>
                          </div>
                        ) : field.type === 'section' || field.type === 'split_workflow' ? (
                          <EditableSectionRenderer
                            field={field}
                            value={editedResponses[field.id]?.value || { instances: [] }}
                            readOnly={false}
                            onChange={(v) => handleFieldEdit(field.id, v)}
                          />
                        ) : field.type === 'signature' ? (
                          <div>
                            <div className="text-sm font-semibold mb-2">Signature History (Append-Only):</div>
                            {((currentValue?.instances || (currentValue ? [currentValue] : [])).filter(Boolean)).map((sig, i) => {
                              const url = typeof sig === 'string' ? sig : sig.url;
                              return (
                                <div key={i} className="mb-3 p-3 border rounded bg-gray-50">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xs text-gray-600">Version {i + 1}</span>
                                    {sig.createdAt && (
                                      <span className="text-xs text-gray-500">
                                        {new Date(sig.createdAt).toLocaleString()}
                                      </span>
                                    )}
                                    {sig.createdBy && (
                                      <span className="text-xs text-gray-500">by {sig.createdBy}</span>
                                    )}
                                    {sig.afterSubmission && (
                                      <span className="px-2 py-0.5 bg-orange-200 text-orange-800 text-xs rounded">
                                        Added after submission
                                      </span>
                                    )}
                                  </div>
                                  {sig.reason && (
                                    <div className="text-xs text-gray-600 mb-2 italic">Reason: {sig.reason}</div>
                                  )}
                                  <img 
                                    src={url}
                                    alt={`Signature ${i + 1}`}
                                    className="max-w-xs border rounded"
                                    style={{ maxHeight: '150px' }}
                                  />
                                </div>
                              );
                            })}
                            <button
                              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                              onClick={() => {
                                setCurrentSignatureField(field);
                                setSignatureModalOpen(true);
                              }}
                            >
                              ✍️ Add New Signature
                            </button>
                            <div className="text-xs text-blue-600 italic mt-2">
                              💡 Signatures are append-only for audit integrity. Old signatures remain visible.
                            </div>
                          </div>
                        ) : field.type === 'media' || field.type === 'photo' ? (
                          <div>
                            <div className="text-sm font-semibold mb-2">Uploaded Files (Append-Only):</div>
                            {((currentValue?.files || [currentValue]).filter(Boolean)).map((file, i) => {
                              const url = typeof file === 'string' ? file : file.url;
                              const name = typeof file === 'object' ? file.name : `File ${i + 1}`;
                              return (
                                <div key={i} className="mb-3 p-3 border rounded bg-gray-50">
                                  <div className="text-xs text-gray-500 mb-1">
                                    {name}
                                    {file.uploadedAt ? ` - ${new Date(file.uploadedAt).toLocaleString()}` : ''}
                                    {file.uploadedBy ? ` by ${file.uploadedBy}` : ''}
                                  </div>
                                  <img 
                                    src={url}
                                    alt={name}
                                    className="max-w-xs border rounded"
                                    style={{ maxHeight: '200px' }}
                                  />
                                </div>
                              );
                            })}
                            <div className="text-sm text-blue-600 italic mt-2">
                              💡 New files must be uploaded via form submission.
                              <br/>Contact admin to add inline file upload capability.
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 italic">
                            Field type "{field.type}" cannot be edited (current value: {String(currentValue || 'N/A')})
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div id="premium-report-view" className="text-gray-900">
                  {hiddenWorkflowCount > 0 && (
                    <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                      Workflow visibility is active for this report. {hiddenWorkflowCount} section{hiddenWorkflowCount === 1 ? "" : "s"} hidden for your account.
                    </div>
                  )}
                  <PremiumReportRenderer
                    formDef={visibleReportFields}
                    responses={editMode ? editedResponses : (selectedReport.responses || selectedReport.answers || {})}
                    changeLog={selectedReport.changeLog || []}
                    remediation={selectedReport.remediation || {}}
                    onAddNote={(fieldId) => {
                      setCurrentNoteField(fieldId);
                      setNoteModalOpen(true);
                    }}
                    onAddAttachment={handleAddAttachment}
                    readOnly={!canRemediateReport()}
                  />
                </div>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2">
              {/* Remediation Sign-off Button */}
              {canRemediateReport() && !selectedReport.remediationSignoff?.signed && (
                <button
                  onClick={() => setSignoffModalOpen(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded shadow-sm hover:bg-green-700 font-semibold"
                >
                  ✔ Sign Off Remediation
                </button>
              )}
              
              {/* Sign-off Status Badge */}
              {selectedReport.remediationSignoff?.signed && (
                <div className="px-4 py-3 bg-green-100 border border-green-400 rounded">
                  <div className="text-green-800 font-semibold mb-2">
                    ✔ Signed off by {selectedReport.remediationSignoff.signedBy} on {
                      selectedReport.remediationSignoff.signedAt?.seconds
                        ? new Date(selectedReport.remediationSignoff.signedAt.seconds * 1000).toLocaleString()
                        : new Date(selectedReport.remediationSignoff.signedAt).toLocaleString()
                    }
                  </div>
                  {selectedReport.remediationSignoff.signatureUrl && (
                    <div className="mt-2">
                      <div className="text-xs text-green-700 mb-1">Signature:</div>
                      <img 
                        src={selectedReport.remediationSignoff.signatureUrl} 
                        alt="Signature" 
                        className="border border-green-300 rounded bg-white p-2"
                        style={{ maxWidth: '300px', maxHeight: '100px' }}
                      />
                    </div>
                  )}
                  {selectedReport.remediationSignoff.comment && (
                    <div className="mt-2 text-sm text-green-700">
                      <span className="font-semibold">Comment:</span> {selectedReport.remediationSignoff.comment}
                    </div>
                  )}
                </div>
              )}
              
              {editMode && (
                <>
                  <button
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                    onClick={saveEditedReport}
                  >
                    💾 Save Changes
                  </button>
                  <button
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
                    onClick={() => {
                      setEditMode(false);
                      setEditedResponses(selectedReport.responses || selectedReport.answers || {});
                    }}
                  >
                    ✖ Cancel
                  </button>
                </>
              )}
              
              {!editMode && (
                <>
                  <button
                    className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded"
                    onClick={() => printReport(selectedReport)}
                  >
                    Export as PDF
                  </button>
                  <button 
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded" 
                    onClick={() => {
                      setSelectedReport(null);
                      setEditMode(false);
                      setShowChangeLog(false);
                    }}
                  >
                    ← Back to Reports
                  </button>
                  <button
                    className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded"
                    onClick={() => window.location.href = "https://buildmychecks.com/home"}
                  >
                    🏠 Home
                  </button>
                </>
              )}
            </div>
          </div>
        ) : completedChecks.length === 0 ? (
          <div className="text-gray-500">{t('reports.noChecksFound')}</div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 text-gray-900 dark:text-gray-100">
            <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
              <input
                type="text"
                placeholder={t('reports.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full md:w-1/3 px-4 py-2 rounded border shadow-sm dark:bg-gray-700 dark:text-white"
              />

              <select
                value={filterFormId}
                onChange={(e) => { setFilterFormId(e.target.value); setPage(1); }}
                className="px-4 py-2 rounded border shadow-sm dark:bg-gray-700 dark:text-white"
              >
                <option value="all">{t('reports.allForms')}</option>
                {uniqueForms.map(fid => {
                  const form = forms.find(f => f.id === fid);
                  const formTitle = form?.title || form?.formTitle || form?.name || fid;
                  return <option key={fid} value={fid}>{formTitle}</option>;
                })}
              </select>

              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                className="px-4 py-2 rounded border shadow-sm dark:bg-gray-700 dark:text-white"
              >
                <option value="all">{t('reports.allStatuses')}</option>
                <option value="submitted">{t('reports.submitted')}</option>
                <option value="approved">{t('reports.approved')}</option>
                <option value="failed">{t('reports.failed')}</option>
              </select>

              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="px-4 py-2 rounded border shadow-sm dark:bg-gray-700 dark:text-white"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="px-4 py-2 rounded border shadow-sm dark:bg-gray-700 dark:text-white"
              />
            </div>

            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <button className="px-4 py-2 bg-purple-600 text-white rounded shadow hover:bg-purple-700" onClick={handlePrintSelected}>
                  🖨️ Print / Save PDF ({selectedIds.length} report{selectedIds.length > 1 ? 's' : ''})
                </button>
                {canEditReports() && (
                  <button className="px-4 py-2 bg-red-600 text-white rounded shadow hover:bg-red-700" onClick={handleDeleteSelected}>
                    🗑️ Delete Selected ({selectedIds.length})
                  </button>
                )}
              </div>
            )}

            <table className="w-full text-left">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="py-3 px-4">
                    <input
                      type="checkbox"
                      checked={paginatedChecks.length > 0 && paginatedChecks.every(c => selectedIds.includes(c.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Add all IDs from current page to selection
                          const pageIds = paginatedChecks.map(c => c.id);
                          setSelectedIds(prev => [...new Set([...prev, ...pageIds])]);
                        } else {
                          // Remove all IDs from current page from selection
                          const pageIds = paginatedChecks.map(c => c.id);
                          setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
                        }
                      }}
                    />
                  </th>
                  <th className="py-3 px-4 font-semibold">{t('reports.colTitle')}</th>
                  <th className="py-3 px-4 font-semibold">{t('reports.colDate')}</th>
                  <th className="py-3 px-4 font-semibold">{t('reports.colStatus')}</th>
                  <th className="py-3 px-4 font-semibold text-right">{t('reports.colPreview')}</th>
                  <th className="py-3 px-4 font-semibold text-right">{t('reports.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedChecks.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-4 text-center text-gray-500">No matching reports found.</td>
                  </tr>
                ) : (
                  paginatedChecks.map((check) => (
                    <tr key={check.id} className="border-t hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(check.id)}
                          onChange={(e) => { e.stopPropagation(); toggleSelect(check.id); }}
                        />
                      </td>
                      <td className="py-3 px-4">{getTitle(check)}</td>
                      <td className="py-3 px-4">{formatDate(check.createdAt || check.date || check.submittedAt)}</td>
                      <td className="py-3 px-4 capitalize">{getStatus(check)}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded shadow"
                          onClick={(e) => { e.stopPropagation(); setQuickPreview(check); }}
                        >
                          Quick View ▶
                        </button>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {canEditReports() && (
                          <button
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded shadow"
                            onClick={(e) => { e.stopPropagation(); handleDeleteSubmission(check); }}
                            title="Delete submission (admin only)"
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-600">Page {currentPage} of {totalPages}</div>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 rounded border dark:border-gray-600"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(Math.max(1, currentPage - 1))}
                >
                  ◀ Prev
                </button>
                <button
                  className="px-3 py-1 rounded border dark:border-gray-600"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                >
                  Next ▶
                </button>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="px-2 py-1 rounded border dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
      {quickPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg p-6 w-full max-w-3xl shadow-xl relative overflow-y-auto max-h-[90vh]">
            <button
              className="absolute top-3 right-3 text-xl"
              onClick={() => setQuickPreview(null)}
            >
              ✖
            </button>

            <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
              {getTitle(quickPreview)}
            </h2>

            <div className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              {formatDate(quickPreview.createdAt || quickPreview.date || quickPreview.submittedAt)}
              {" • "}
              {getStatus(quickPreview)}
            </div>

            <PremiumReportRenderer
              formDef={
                filterVisibleWorkflowFields(forms.find(f => f.id === quickPreview.formId)?.fields || [], userProfile)
              }
              responses={quickPreview.responses || quickPreview.answers || {}}
              changeLog={quickPreview.changeLog || []}
              remediation={quickPreview.remediation || {}}
              readOnly={true}
            />

            <div className="flex justify-end gap-3 mt-6">
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded"
                onClick={() => setQuickPreview(null)}
              >
                Close
              </button>
              <button
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                onClick={() => {
                  printReport(quickPreview);
                }}
              >
                🖨️ Print
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded"
                onClick={() => {
                  loadReportWithForm(quickPreview);
                  setQuickPreview(null);
                }}
              >
                View Full Report
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded"
                onClick={async () => {
                  const fullHtml = await loadFullReportIntoContainer(quickPreview);
                  if (fullHtml) printHTML(fullHtml, getPdfFilename(quickPreview));
                }}
              >
                Export PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signature Modal */}
      <SignatureModal
        isOpen={signatureModalOpen}
        onClose={() => {
          setSignatureModalOpen(false);
          setCurrentSignatureField(null);
        }}
        onSave={handleAddSignature}
        fieldLabel={currentSignatureField?.label || currentSignatureField?.question || "Signature"}
      />

      {/* Remediation Note Modal */}
      <RemediationNoteModal
        open={noteModalOpen}
        onClose={() => {
          setNoteModalOpen(false);
          setCurrentNoteField(null);
        }}
        onSave={(text) => handleAddNote(currentNoteField, text)}
        fieldLabel={
          currentNoteField && selectedReport?.formDef
            ? selectedReport.formDef.find(f => f.id === currentNoteField)?.label || currentNoteField
            : ''
        }
      />

      {/* Remediation Sign-off Modal */}
      <RemediationSignoffModal
        open={signoffModalOpen}
        onClose={() => setSignoffModalOpen(false)}
        onSave={handleSignoff}
      />

      {/* Upload indicator */}
      {uploadingAttachment && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
          📎 Uploading attachment...
        </div>
      )}
    </div>
    </div>
  );
}
